import {
  Alert,
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Input,
  Menu,
  Portal,
  Table,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { Info, Trash2 } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { useDebouncedCallback } from '@mangovoice/core'
import type { ToastLike } from '@mangovoice/mango-ui'
import {
  ConfirmDialog,
  ListToolbar,
  LoadingState,
  SortableColumnHeader,
} from '@mangovoice/mango-ui'
import type { MessageGroup } from '@mangovoice/types'

import type {
  MessageGroupListParams,
  MessagingApi,
} from '../api/messagingApi.types'
import { CreateMessageGroupModal } from '../components/CreateMessageGroupModal'
import {
  useCreateMessageGroup,
  useDeleteMessageGroup,
  useGetAvailableSmsNumbers,
  useGetMessageGroupMembers,
  useGetMessageGroups,
} from '../queries/messagingQueries'
import type { MessagingTranslateFn } from '../types/i18n.types'
import { text } from '../types/i18n.types'

const DEFAULT_PAGE_SIZE = 25
const ALL_LIMIT = 10000

type SortField = 'name' | 'id' | 'primary_phone_number' | 'updated_at'
type SortDirection = 'asc' | 'desc'

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return 'N/A'
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'N/A'
  }
}

const getPrimarySmsDisplay = (group: MessageGroup): string => {
  const primary = group.phone_numbers.find((p) => p.is_primary_sms)
  return primary?.number_for_display || '--'
}

const hasSmsEnabled = (group: MessageGroup): boolean =>
  group.phone_numbers.length > 0

// -- Sub-components --

const ListLoadingState: React.FC<{ t?: MessagingTranslateFn }> = ({ t }) => (
  <LoadingState
    layout="alert"
    title={text(t, 'messaging.list.loading', 'Loading message groups...')}
  />
)

const ErrorState: React.FC<{ error: Error; t?: MessagingTranslateFn }> = ({
  error,
  t,
}) => (
  <Alert.Root status="error" p={4}>
    <Alert.Indicator />
    <Box>
      <Text fontWeight="bold">{text(t, 'messaging.list.error', 'Error')}</Text>
      <Text>{error.message}</Text>
    </Box>
  </Alert.Root>
)

const EmptyState: React.FC<{ t?: MessagingTranslateFn }> = ({ t }) => (
  <Alert.Root status="info" p={4}>
    <Alert.Indicator />
    <Box>
      <Text fontWeight="bold">
        {text(t, 'messaging.list.empty', 'No message groups found')}
      </Text>
      <Text>
        {text(
          t,
          'messaging.list.emptyDescription',
          'There are currently no message groups configured for this PBX.',
        )}
      </Text>
    </Box>
  </Alert.Root>
)

const MemberCountCell: React.FC<{
  api: MessagingApi
  groupId: number
  t?: MessagingTranslateFn
}> = ({ api, groupId, t }) => {
  const { data: members, isLoading } = useGetMessageGroupMembers(api, groupId)

  if (isLoading) return <Text fontSize="sm">...</Text>

  const count = members?.length ?? 0
  if (count === 0) {
    return (
      <HStack gap={1}>
        <Text fontSize="sm">0</Text>
        <Tooltip.Root
          openDelay={200}
          closeDelay={0}
          positioning={{
            placement: 'top',
          }}
        >
          <Tooltip.Trigger asChild>
            <Icon asChild color="fg.warning" cursor="pointer">
              <Info size={14} />
            </Icon>
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              {text(
                t,
                'messaging.list.noMembersWarning',
                'This group has no members. Customer messages may go unread.',
              )}
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      </HStack>
    )
  }
  return <Text fontSize="sm">{count}</Text>
}

const ThreeDotsIcon: React.FC = () => (
  <Icon boxSize={4}>
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  </Icon>
)

const MessageGroupRow: React.FC<{
  api: MessagingApi
  group: MessageGroup
  getDetailPath: (groupId: number) => string
  onRequestDelete: (id: number, name: string) => void
  canDelete: boolean
  t?: MessagingTranslateFn
}> = ({ api, group, getDetailPath, onRequestDelete, canDelete, t }) => {
  const navigate = useNavigate()
  const detailPath = getDetailPath(group.id)

  const handleRowClick = () => {
    navigate(detailPath)
  }

  return (
    <Table.Row
      cursor="pointer"
      _hover={{ bg: 'bg.subtle' }}
      onClick={handleRowClick}
    >
      <Table.Cell px={4} py={3}>
        <Link
          to={detailPath}
          style={{ textDecoration: 'none' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Text
            fontWeight="medium"
            color="fg.info"
            _hover={{ textDecoration: 'underline' }}
          >
            {group.name}
          </Text>
        </Link>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm">{getPrimarySmsDisplay(group)}</Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <MemberCountCell api={api} groupId={group.id} t={t} />
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        {hasSmsEnabled(group) ? (
          <Badge colorPalette="success" px={2} py={1}>
            {text(t, 'messaging.list.smsEnabled', 'Enabled')}
          </Badge>
        ) : (
          <Badge colorPalette="neutral" px={2} py={1}>
            {text(t, 'messaging.list.smsDisabled', 'Disabled')}
          </Badge>
        )}
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {formatDate(group.created_at)}
        </Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {formatDate(group.updated_at)}
        </Text>
      </Table.Cell>
      {canDelete && (
        <Table.Cell px={4} py={3} onClick={(e) => e.stopPropagation()}>
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button size="sm" variant="outline" px={2}>
                <ThreeDotsIcon />
              </Button>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.Item
                    value="delete"
                    onClick={() => onRequestDelete(group.id, group.name)}
                    color="fg.error"
                    px={4}
                    py={2}
                  >
                    <Icon asChild mr={1}>
                      <Trash2 size={16} />
                    </Icon>
                    {text(t, 'messaging.list.delete', 'Delete')}
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </Table.Cell>
      )}
    </Table.Row>
  )
}

const MessageGroupsTable: React.FC<{
  api: MessagingApi
  groups: MessageGroup[]
  getDetailPath: (groupId: number) => string
  onRequestDelete: (id: number, name: string) => void
  canDelete: boolean
  sortField: SortField | null
  sortDirection: SortDirection
  onSort: (field: string, direction: SortDirection) => void
  t?: MessagingTranslateFn
}> = ({
  api,
  groups,
  getDetailPath,
  onRequestDelete,
  canDelete,
  sortField,
  sortDirection,
  onSort,
  t,
}) => (
  <Box overflowX="auto">
    <Table.Root variant="outline">
      <Table.Header>
        <Table.Row>
          <SortableColumnHeader
            label={text(t, 'messaging.list.columnGroupName', 'Group Name')}
            field="name"
            sortField={sortField}
            direction={sortDirection}
            onSortChange={onSort}
          />
          <SortableColumnHeader
            label={text(t, 'messaging.list.columnPrimarySms', 'Primary SMS')}
            field="primary_phone_number"
            sortField={sortField}
            direction={sortDirection}
            onSortChange={onSort}
          />
          <Table.ColumnHeader px={4} py={3}>
            {text(t, 'messaging.list.columnMembers', 'Members')}
          </Table.ColumnHeader>
          <Table.ColumnHeader px={4} py={3}>
            {text(t, 'messaging.list.columnSmsStatus', 'SMS Status')}
          </Table.ColumnHeader>
          <Table.ColumnHeader px={4} py={3}>
            {text(t, 'messaging.list.columnCreated', 'Created')}
          </Table.ColumnHeader>
          <SortableColumnHeader
            label={text(t, 'messaging.list.columnUpdated', 'Updated')}
            field="updated_at"
            sortField={sortField}
            direction={sortDirection}
            onSortChange={onSort}
          />
          {canDelete && <Table.ColumnHeader px={4} py={3} w="60px" />}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {groups.map((group) => (
          <MessageGroupRow
            key={group.id}
            api={api}
            group={group}
            getDetailPath={getDetailPath}
            onRequestDelete={onRequestDelete}
            canDelete={canDelete}
            t={t}
          />
        ))}
      </Table.Body>
    </Table.Root>
  </Box>
)

// -- Main page component --

export interface MessagingListPageProps {
  /** Messaging API instance */
  api: MessagingApi
  /** PBX ID */
  pbxId: number
  /** Toast notifications */
  toast: ToastLike
  /** Function to build a detail page path for a given group ID */
  getDetailPath: (groupId: number) => string
  /**
   * Convenience permission prop that gates every management action
   * (create, delete). Granular props below take precedence when provided.
   * Defaults to `false` — consumers must explicitly opt in to management
   * controls. This mirrors the legacy admin's server-driven `user_can`
   * gates on MESSAGING and prevents a missing host-level check from
   * accidentally exposing billable actions.
   */
  canManageMessageGroups?: boolean
  /** Gates the "+ Create Group" button and create modal. Falls back to `canManageMessageGroups`. */
  canCreate?: boolean
  /** Gates the per-row delete menu and delete-confirm dialog. Falls back to `canManageMessageGroups`. */
  canDelete?: boolean
  /** Optional translation function for i18n; when omitted, components use fallback English */
  t?: MessagingTranslateFn
}

export const MessagingListPage: React.FC<MessagingListPageProps> = ({
  api,
  pbxId,
  toast,
  getDetailPath,
  canManageMessageGroups = false,
  canCreate,
  canDelete,
  t,
}) => {
  const effectiveCanCreate = canCreate ?? canManageMessageGroups
  const effectiveCanDelete = canDelete ?? canManageMessageGroups
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<{
    id: number
    name: string
  } | null>(null)
  const [pageSize, setPageSize] = useState<number | 'All'>(DEFAULT_PAGE_SIZE)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortField, setSortField] = useState<SortField | null>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const applySearch = useDebouncedCallback((value: string) => {
    setDebouncedSearch(value)
    setCurrentPage(1)
  }, 300)

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value)
      applySearch(e.target.value)
    },
    [applySearch],
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [pageSize])

  const listParams = useMemo((): MessageGroupListParams => {
    const params: MessageGroupListParams = {}
    if (pageSize !== 'All') {
      params.limit = pageSize as number
      params.offset = (currentPage - 1) * (pageSize as number)
    } else {
      params.limit = ALL_LIMIT
      params.offset = 0
    }
    if (sortField) {
      params.ordering = sortDirection === 'desc' ? `-${sortField}` : sortField
    }
    if (debouncedSearch) {
      params.search = debouncedSearch
    }
    return params
  }, [pageSize, currentPage, sortField, sortDirection, debouncedSearch])

  const { data, isLoading, error } = useGetMessageGroups(api, pbxId, listParams)
  const { data: availableSmsNumbers = [] } = useGetAvailableSmsNumbers(
    api,
    pbxId,
  )
  const createMutation = useCreateMessageGroup(api, pbxId)
  const deleteMutation = useDeleteMessageGroup(api, pbxId)

  const allResults = data?.results ?? []
  const groups = allResults
  const totalCount = data?.count ?? 0

  const handleSort = (field: string, direction: SortDirection) => {
    setSortField(field as SortField)
    setSortDirection(direction)
    setCurrentPage(1)
  }

  const handleCreate = (name: string, primaryPhoneNumberId: number) => {
    createMutation.mutate(
      { name, pbx_id: pbxId, primary_phone_number_id: primaryPhoneNumberId },
      {
        onSuccess: () => {
          toast.create({
            title: text(
              t,
              'messaging.list.toast.createdTitle',
              'Message group created',
            ),
            description: text(
              t,
              'messaging.list.toast.createdDescription',
              `"${name}" has been created successfully.`,
              { name },
            ),
            type: 'success',
            duration: 5000,
            closable: true,
          })
          setIsCreateModalOpen(false)
        },
        onError: () => {
          toast.create({
            title: text(
              t,
              'messaging.list.toast.createFailedTitle',
              'Create failed',
            ),
            description: text(
              t,
              'messaging.list.toast.createFailedDescription',
              'Failed to create message group',
            ),
            type: 'error',
            duration: 5000,
            closable: true,
          })
        },
      },
    )
  }

  const handleDelete = (id: number, name: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        setPendingDelete(null)
        toast.create({
          title: text(
            t,
            'messaging.list.toast.deletedTitle',
            'Message group deleted',
          ),
          description: text(
            t,
            'messaging.list.toast.deletedDescription',
            `"${name}" has been deleted.`,
            { name },
          ),
          type: 'success',
          duration: 5000,
          closable: true,
        })
      },
      onError: () => {
        toast.create({
          title: text(
            t,
            'messaging.list.toast.deleteFailedTitle',
            'Delete failed',
          ),
          description: text(
            t,
            'messaging.list.toast.deleteFailedDescription',
            'Failed to delete message group',
          ),
          type: 'error',
          duration: 5000,
          closable: true,
        })
      },
    })
  }

  const PaginationToolbar: React.FC<{ mb?: number }> = ({ mb }) => (
    <Box mb={mb}>
      <ListToolbar
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        currentPage={currentPage}
        totalCount={totalCount}
        onPageChange={setCurrentPage}
        isLoading={isLoading}
        showToolbar={totalCount > 0}
        pageSizeOptions={[25, 50, 100]}
        t={t}
        i18nPrefix="messaging.pagination"
      />
    </Box>
  )

  const renderContent = () => {
    if (isLoading) return <ListLoadingState t={t} />
    if (error) return <ErrorState error={error as Error} t={t} />
    if (!groups || groups.length === 0) {
      if (debouncedSearch) {
        return (
          <Alert.Root status="info" p={4}>
            <Alert.Indicator />
            <Box>
              <Text fontWeight="bold">
                {text(t, 'messaging.list.noResults', 'No results')}
              </Text>
              <Text>
                {text(
                  t,
                  'messaging.list.noResultsDescription',
                  `No message groups matching "${debouncedSearch}".`,
                  { search: debouncedSearch },
                )}
              </Text>
            </Box>
          </Alert.Root>
        )
      }
      return <EmptyState t={t} />
    }
    return (
      <MessageGroupsTable
        api={api}
        groups={groups}
        getDetailPath={getDetailPath}
        onRequestDelete={(id, name) => setPendingDelete({ id, name })}
        canDelete={effectiveCanDelete}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        t={t}
      />
    )
  }

  return (
    <Box flex="1" w="full" p={6} overflow="auto">
      <Box display="flex" justifyContent="space-between" alignItems="start">
        <Heading fontSize="2xl">
          {text(t, 'messaging.list.title', 'Messaging')}
        </Heading>
        {effectiveCanCreate && (
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            colorPalette="brand"
            bg="brand.solid"
            color="brand.contrast"
            borderRadius="lg"
            _hover={{ bg: 'brand.emphasized' }}
            _active={{ bg: 'brand.emphasized' }}
            css={{
              '& svg': { stroke: 'var(--chakra-colors-brand-contrast)' },
            }}
          >
            {text(t, 'messaging.list.createButton', '+ Create Group')}
          </Button>
        )}
      </Box>
      <Box my={4} maxW="sm">
        <Input
          placeholder={text(
            t,
            'messaging.list.searchPlaceholder',
            'Search by name or phone number...',
          )}
          value={searchInput}
          onChange={handleSearchChange}
          size="sm"
          borderRadius="md"
        />
      </Box>
      <PaginationToolbar mb={4} />
      {renderContent()}
      <PaginationToolbar />

      {effectiveCanCreate && (
        <CreateMessageGroupModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          pbxId={pbxId}
          onSave={handleCreate}
          isSaving={createMutation.isPending}
          toast={toast}
          t={t}
          availableSmsNumbers={availableSmsNumbers}
        />
      )}

      {effectiveCanDelete && pendingDelete && (
        <ConfirmDialog
          open={true}
          onClose={() => setPendingDelete(null)}
          title={text(
            t,
            'messaging.list.deleteConfirmTitle',
            'Delete message group',
          )}
          description={text(
            t,
            'messaging.list.deleteConfirm',
            `Are you sure you want to delete "${pendingDelete.name}"?`,
            { name: pendingDelete.name },
          )}
          confirmLabel={text(t, 'messaging.list.delete', 'Delete')}
          cancelLabel={text(t, 'messaging.createModal.cancel', 'Cancel')}
          onConfirm={() => handleDelete(pendingDelete.id, pendingDelete.name)}
          variant="destructive"
          isLoading={deleteMutation.isPending}
        />
      )}
    </Box>
  )
}
